/**
 * SafeRouteLayer - Green/Yellow/Red route visualization
 * Shows cognitive-optimized navigation paths
 */

import { Source, Layer, type LineLayer } from 'react-map-gl';
import type { RouteGeoJSON } from '@/types';

interface SafeRouteLayerProps {
  routeGeoJSON: RouteGeoJSON;
  safetyLevel: 'safe' | 'moderate' | 'hazardous';
}

const ROUTE_COLORS = {
  safe: {
    main: '#00ff88',
    glow: 'rgba(0, 255, 136, 0.4)',
  },
  moderate: {
    main: '#ffdd00',
    glow: 'rgba(255, 221, 0, 0.4)',
  },
  hazardous: {
    main: '#ff3366',
    glow: 'rgba(255, 51, 102, 0.4)',
  },
};

export function SafeRouteLayer({ routeGeoJSON, safetyLevel }: SafeRouteLayerProps) {
  const colors = ROUTE_COLORS[safetyLevel];

  // Glow effect layer (wider, blurred)
  const glowLayer: LineLayer = {
    id: 'safe-route-glow',
    type: 'line',
    source: 'safe-route',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': colors.glow,
      'line-width': 16,
      'line-blur': 8,
    },
  };

  // Main route line
  const mainLineLayer: LineLayer = {
    id: 'safe-route-line',
    type: 'line',
    source: 'safe-route',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': colors.main,
      'line-width': 4,
    },
  };

  // Animated dash pattern (marching ants effect)
  const animatedLayer: LineLayer = {
    id: 'safe-route-animated',
    type: 'line',
    source: 'safe-route',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#ffffff',
      'line-width': 2,
      'line-dasharray': [0.5, 3],
    },
  };

  // Direction arrows
  const arrowLayer: LineLayer = {
    id: 'safe-route-arrows',
    type: 'line',
    source: 'safe-route',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#ffffff',
      'line-width': 1,
      'line-dasharray': [0, 2, 1],
    },
  };

  return (
    <Source id="safe-route" type="geojson" data={routeGeoJSON}>
      <Layer {...glowLayer} />
      <Layer {...mainLineLayer} />
      <Layer {...animatedLayer} />
      <Layer {...arrowLayer} />
    </Source>
  );
}

export default SafeRouteLayer;
